import { Button, Card } from "@jhool-io/fe-components";
import { Link, useNavigate } from "react-router-dom";
import {
    cn,
    convertTimeTo12HoursFormat,
    displayNameInRightFormat,
    formatDate,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../../../utils/helpers";
import { IWeeklySchedule } from "../../../types/schedule.types";
import { ClaimStatus } from "../../../../claims/types/claims.types";
import { NoteStatus } from "../../../../notes/types/notes.types";
import { BillStatus } from "../../../../billing/types/billing.types";
import { getStatusTag } from "../../../../claims/helpers/claims.helper";
import { getNoteStatusTag } from "../../../../notes/helpers/notes.helpers";
import { getBillStatusTag } from "../../../../billing/helpers/billing.helpers";
import UserIcon from "../../../../../icons/User";
import ArrowUpRightIcon from "../../../../../icons/ArrowUpRight";

interface BillerScheduleDetailsProps {
    schedule: IWeeklySchedule;
    borderLeftColor: string;
}

export default function BillerScheduleDetails({
    schedule,
    borderLeftColor,
}: BillerScheduleDetailsProps) {
    const navigate = useNavigate();
    return (
        <Card classNames="[border-left-width:2px]" style={{ borderLeftColor }}>
            <div className="flex flex-col">
                <div className="flex items-start gap-x-[--spacing-items]">
                    <UserIcon className="size-5 stroke-textSec stroke-[1px]" />
                    <div className="flex flex-col">
                        <span className="font-bold text-title mb-[--spacing-items] leading-none">
                            {`${displayNameInRightFormat({
                                firstName: schedule.client.first_name,
                                lastName: schedule.client.last_name,
                            })} X ${displayNameInRightFormat({
                                firstName: schedule.provider.first_name,
                                lastName: schedule.provider.last_name,
                            })}`}
                        </span>
                        <span className="font-medium text-base text-textSec mb-2 capitalize flex items-center gap-x-2">
                            <span>
                                {removeEnumUnderscore(schedule.service)}
                            </span>
                            •
                            <span className="uppercase">
                                {schedule.client.insurance_provider_name}
                            </span>
                        </span>
                        <span className="font-medium text-base text-textSec mb-2">
                            {formatDate(
                                schedule.possible_start_date_time as string
                            )}
                            ,{" "}
                            {`${convertTimeTo12HoursFormat(
                                `${schedule.session_start_hr}:${schedule.session_start_min}`
                            ).toUpperCase()} - ${convertTimeTo12HoursFormat(
                                `${schedule.session_end_hr}:${schedule.session_end_min}`
                            ).toUpperCase()}`}
                        </span>
                        <div className="flex font-medium text-base text-textSec gap-2">
                            {schedule.coinsurance_amount ? (
                                <>
                                    <div className="font-bold">COINS</div>
                                    {showMoneyInAppFormat(
                                        schedule?.coinsurance_amount || "--"
                                    )}
                                    ,
                                </>
                            ) : null}{" "}
                            {schedule.coinsurance_amount_paid ? (
                                <>
                                    <div className="font-bold">PAID</div>
                                    {showMoneyInAppFormat(
                                        schedule?.coinsurance_amount_paid ||
                                            "--"
                                    )}
                                </>
                            ) : null}
                        </div>

                        <div className="flex pt-2 gap-2">
                            {schedule.note_status && (
                                <span>
                                    {getNoteStatusTag(
                                        schedule.note_status as NoteStatus,
                                        "Note:"
                                    )}
                                </span>
                            )}
                            {schedule.invoice_status && (
                                <span>
                                    {getBillStatusTag(
                                        schedule.invoice_status as BillStatus,
                                        "Bill:"
                                    )}
                                </span>
                            )}
                        </div>
                        {schedule.claim_status && (
                            <div className="mt-2">
                                {getStatusTag(
                                    schedule.claim_status as ClaimStatus,
                                    "Claim:"
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="mt-[--spacing-items]">
                {schedule.note_id && (
                    <Button
                        variant="normal"
                        size="auto"
                        className=" w-full rounded-md border border-strokedark h-8 bg-white text-textSec text-caption font-semibold [border:1px_solid_var(--color-stroke)]"
                        onClick={() =>
                            navigate(
                                `/billing-and-claims/${schedule.client.client_id}/${schedule.note_id}`
                            )
                        }
                    >
                        View bill
                    </Button>
                )}
            </div>
            {schedule.recent_sessions.length > 0 && (
                <div className="mb-[19px]">
                    <div className="flex items-start gap-x-2">
                        <svg
                            width="20"
                            height="18"
                            viewBox="0 0 20 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M8.24263 9.2931C10.7232 9.2931 12.734 7.3247 12.734 4.89655C12.734 2.4684 10.7232 0.5 8.24263 0.5C5.76209 0.5 3.75122 2.4684 3.75122 4.89655C3.75122 7.3247 5.76209 9.2931 8.24263 9.2931Z"
                                stroke="var(--color-text-sec)"
                                strokeLinecap="round"
                            />
                            <path
                                d="M1 16.0369C2.55463 13.7413 5.21833 12.2266 8.24255 12.2266"
                                stroke="var(--color-text-sec)"
                                strokeLinecap="round"
                            />
                            <path
                                d="M16.5367 17.5024H9.14087L11.3782 12.5748C11.474 12.3626 11.6884 12.2266 11.9255 12.2266H18.4004C18.8339 12.2266 19.1238 12.6627 18.9477 13.0508L17.0841 17.1542C16.9883 17.3664 16.7739 17.5024 16.5367 17.5024Z"
                                stroke="var(--color-text-sec)"
                                strokeLinecap="round"
                            />
                            <path
                                d="M9.14098 17.5H5.54785"
                                stroke="var(--color-text-sec)"
                                strokeLinecap="round"
                            />
                        </svg>

                        <div>
                            <div className="mb-2">
                                <span className="text-base font-semibold">
                                    Last 3 sessions
                                </span>
                            </div>
                            <div className="flex flex-col mb-4">
                                {schedule.recent_sessions.map((session) => (
                                    <div
                                        key={`${session.note_id} ${session.insurance_amount}`}
                                        className="bg-card-bg rounded-md pb-2 flex items-center gap-x-2 text-base text-textSec"
                                    >
                                        <span>
                                            {formatDate(
                                                session.date_of_service as string
                                            )}
                                        </span>{" "}
                                        •{" "}
                                        <span>
                                            {formatDate(
                                                session.date_of_service as string,
                                                false,
                                                "hh:mm a"
                                            )}
                                        </span>{" "}
                                        •<span>{session.cpt_code || "--"}</span>{" "}
                                        •
                                        <span
                                            className={cn({
                                                "text-danger":
                                                    session.insurance_amount_outstanding <
                                                    0,
                                            })}
                                        >
                                            <span className="font-semibold uppercase">
                                                INS{" "}
                                            </span>
                                            {session.insurance_amount_outstanding <
                                            0
                                                ? `(${showMoneyInAppFormat(
                                                      Math.abs(
                                                          session.insurance_amount_outstanding
                                                      )
                                                  )})`
                                                : showMoneyInAppFormat(
                                                      session.insurance_amount_outstanding
                                                  )}
                                        </span>
                                        •
                                        <span
                                            className={cn({
                                                "text-danger":
                                                    session.coinsurance_amount_outstanding <
                                                    0,
                                            })}
                                        >
                                            <span className="font-semibold uppercase">
                                                COINS{" "}
                                            </span>
                                            {session.coinsurance_amount_outstanding <
                                            0
                                                ? `(${showMoneyInAppFormat(
                                                      Math.abs(
                                                          session.coinsurance_amount_outstanding
                                                      )
                                                  )})`
                                                : showMoneyInAppFormat(
                                                      session.coinsurance_amount_outstanding
                                                  )}
                                        </span>
                                        <Link
                                            to={`/billing-and-claims/${schedule.client.client_id}/${session.note_id}`}
                                            className="link"
                                        >
                                            <ArrowUpRightIcon className="size-4" />
                                        </Link>
                                    </div>
                                ))}
                            </div>
                            <Button
                                variant="can-hover"
                                size="auto"
                                className="w-full rounded-md border border-strokedark h-8 bg-white text-textSec text-caption font-semibold [border:1px_solid_var(--color-stroke)]"
                                onClick={() =>
                                    navigate(
                                        `/clients/${schedule.client.client_id}?tab=claims`
                                    )
                                }
                            >
                                View payment History
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
}
